import { useMemo } from "react";
import useCustomFilters, {
  FilterOption,
} from "components/custom-filters-list/filters/use-custom-filters.hook";

import SelectClinic from "components/custom-filters-list/filters/select-clinic.component";
import * as React from "react";
import { ReportFilter } from "features/reports/types";
import SelectProvider from "components/custom-filters-list/filters/select-provider.component";
import type { AmbientScribeFilters } from "services/types/ambient-scribe.type";
import SelectPatient from "components/custom-filters-list/filters/select-patient.component";

export type ScribesCustomCustomFiltersValues = {
  clinic?: ReportFilter<string>;
  staff?: ReportFilter<string>;
  patient?: ReportFilter<string>;
};

export const SCRIBES_DEFAULT_FILTERS: ScribesCustomCustomFiltersValues = {
  clinic: null,
  staff: null,
  patient: null,
};

type UseScribeCustomFilterOptionsProps = {
  updateCustomFilter: (
    filterName: keyof AmbientScribeFilters,
    filterValue:
      | AmbientScribeFilters[keyof AmbientScribeFilters]
      | undefined
      | null
  ) => void;
  selectedCustomFilters: ScribesCustomCustomFiltersValues;
  setSelectedCustomFilters: React.Dispatch<
    React.SetStateAction<ScribesCustomCustomFiltersValues>
  >;
};

export default function useScribeCustomFilterOptions({
  updateCustomFilter,
  selectedCustomFilters,
  setSelectedCustomFilters,
}: UseScribeCustomFilterOptionsProps) {
  const filterOptions: FilterOption<
    ScribesCustomCustomFiltersValues,
    AmbientScribeFilters
  >[] = useMemo(
    () => [
      {
        name: "staff",
        label: "Staff",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Staff: <b>{metadata.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectProvider
            name="staff"
            control={control}
            label="By Staff"
            autoFocus
            onOptionSelected={(option) => {
              updateFilter(option?.value ?? null);
              setMetadata(option);
            }}
            openOnFocus
            includeOtherClinics
            ignoreRegionsFilter
            ignoreClinicsFilter
          />
        ),
      },
      {
        name: "patient",
        label: "Patient",
        renderViewMode: (filterKey, metadata) => (
          <span>
            By Patient: <b>{metadata?.label}</b>
          </span>
        ),
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectPatient
            name="patient"
            control={control}
            onOptionSelected={(selected) => {
              if (selected) {
                updateFilter(selected.value);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            showInactive
          />
        ),
      },
      {
        name: "clinic",
        label: "Clinic",
        renderViewMode: (filterKey, metadata) => {
          return (
            <span>
              By Clinic: <b>{metadata?.label}</b>
            </span>
          );
        },
        editComponent: (filterKey, setMetadata, control, updateFilter) => (
          <SelectClinic
            name="clinic"
            showAllRegions
            control={control}
            onOptionSelected={(selected) => {
              if (selected && Array.isArray(selected)) {
                const values = selected.map((item) => item.value).join(",");
                updateFilter(values);
              } else if (!selected) {
                updateFilter(null);
              }

              setMetadata(selected);
            }}
            filterByRegions={[]}
          />
        ),
      },
    ],
    []
  );

  const filters = useCustomFilters<
    ScribesCustomCustomFiltersValues,
    AmbientScribeFilters
  >({
    updateCustomFilter,
    filterOptions,
    selectedCustomFilters,
    setSelectedCustomFilters,
  });

  return filters;
}
